import { Listbox, Transition } from '@headlessui/react';
import { ChevronUpDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import { Fragment, useEffect, useState } from 'react';

interface Props {
  data: any[];
  value?: any;
  labelKey?: string;
  valueKey?: string;
  onChange: (item: any) => void;
  disabled?: boolean;
  className?: string;
  minWidth?: number;
  isReadOnly?: boolean;
  displayPattern?: string;
  helperText?: string;
  title?: string;
  showCheckIcon?: boolean;
}

/**
 * basic Select / dropdown component
 */
export default function Select({
  data,
  value,
  labelKey,
  valueKey,
  onChange,
  disabled,
  className,
  minWidth,
  isReadOnly,
  // string value used to view objects values by specified pattern
  // values separated by '-' example: 'attr1-attr2-attr3'
  displayPattern,
  helperText,
  title,
  showCheckIcon = true,
}: Props) {
  const [selected, setSelected] = useState(value ? value : data[0]);
  const onChangeVal = (item: any) => {
    if (!item) return;
    setSelected(item);
    onChange(item);
  };

  useEffect(() => {
    if ((value === null || value === undefined) && !selected) return setSelected(data[0]);
    const item = data.find((x: any) => {
      if (!valueKey) return x.id === value;
      return (
        x[valueKey] && String(x[valueKey]).toLocaleLowerCase() === String(value).toLocaleLowerCase()
      );
    });
    if (item) return setSelected(item);
  }, [value, data]);

  return (
    <div className="inline-flex w-full">
      <Listbox value={selected} onChange={onChangeVal} disabled={isReadOnly}>
        <div className="relative w-full">
          {title && <span className="w-min-max">{title}</span>}
          <Listbox.Button
            style={{ minWidth: minWidth ? minWidth : '100' }}
            className={`relative min-w-fit border my-1 cursor-default rounded-lg bg-white py-2 pl-3 pr-5 text-left shadow-md focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-95 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm ${className}`}
          >
            <span className="block truncate pr-2">
              {displayPattern
                ? displayPattern
                    .split('-')
                    .map((attr: any, index: number) =>
                      index === 0 ? selected[attr] : ' - ' + selected[attr],
                    )
                : labelKey
                ? selected[labelKey]
                : selected}
            </span>
            {!isReadOnly && (
              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center">
                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
              </span>
            )}
          </Listbox.Button>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className="absolute mt-1 max-h-[300px] overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-20 min-w-fit">
              {data.map((itm, idx) => (
                <Listbox.Option
                  key={idx}
                  className={({ active }) =>
                    `relative cursor-default select-none py-2 ${
                      showCheckIcon ? `pl-10` : `pl-4`
                    } pr-2 ${active ? 'bg-amber-100 text-amber-900' : 'text-gray-900'}`
                  }
                  disabled={disabled}
                  value={itm}
                >
                  {({ selected }) => {
                    let displayVal;
                    if (displayPattern) {
                      displayVal = displayPattern
                        .split('-')
                        .map((attr: any, index: number) =>
                          index === 0 ? itm[attr] : ' - ' + itm[attr],
                        );
                    }

                    return (
                      <>
                        <span
                          className={`block truncate ${
                            selected ? 'font-medium' : 'font-normal'
                          } text-left`}
                        >
                          {displayPattern && displayVal
                            ? displayVal
                            : labelKey
                            ? itm[labelKey]
                            : selected.valueOf()}
                        </span>
                        {selected && showCheckIcon ? (
                          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    );
                  }}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
          {helperText && (
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 absolute">{helperText}</p>
          )}
        </div>
      </Listbox>
    </div>
  );
}
